import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { courtApi } from '../services/api';
import { useUserData } from '../hooks/useUserData';
import { useLanguage } from '../contexts/LanguageContext';

const SelectCourtType = () => {
  const [courtTypes, setCourtTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { displayName, isUserAvailable } = useUserData();
  const { t, language } = useLanguage();

  useEffect(() => {
    fetchCourtTypes();
  }, []);

  const fetchCourtTypes = async () => {
    try {
      const data = await courtApi.getCourtTypes();
      setCourtTypes(data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching court types:', err);
      setError(t('failed_to_load_courts'));
      setLoading(false);
    }
  };

  const handleCourtTypeSelect = (courtType) => {
    navigate(`/court-details/${courtType.id}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-40 bg-gradient-to-br from-yellow-400 via-yellow-500 to-yellow-600 p-5 flex flex-col">
        <div className="flex flex-col items-center justify-center h-screen text-white">
          <div className="w-16 h-16 border-4 border-yellow-400 border-opacity-30 border-t-yellow-400 rounded-full animate-spin mb-5"></div>
          <p className="text-xl">{t('loading_data')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen pt-40 bg-gradient-to-br from-yellow-400 via-yellow-500 to-yellow-600 p-5 flex flex-col">
        <div className="flex flex-col items-center justify-center h-screen text-white text-center">
          <p className="text-xl mb-5">{error}</p>
          <button
            onClick={fetchCourtTypes}
            className="bg-gradient-to-r from-gray-500 to-gray-600 border-none text-white px-8 py-4 rounded-full text-lg font-medium transition-all duration-300 hover:from-gray-600 hover:to-gray-700 shadow-lg"
          >
            {t('try_again')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-40 bg-gradient-to-br from-yellow-400 via-yellow-500 to-yellow-600 p-5 flex flex-col relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 -left-4 w-72 h-72 bg-yellow-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
        <div className="absolute top-0 -right-4 w-72 h-72 bg-yellow-200 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse" style={{ animationDelay: '2s' }}></div>
        <div className="absolute -bottom-8 left-20 w-72 h-72 bg-yellow-100 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse" style={{ animationDelay: '4s' }}></div>
      </div>

      <div className="text-center mb-8 text-white relative z-10">
        <h1 className="text-4xl md:text-5xl font-bold mb-3 drop-shadow-lg text-gray-800 font-['Noto Sans Thai', 'Sarabun', 'Kanit', sans-serif]">{t('select_court_type')}</h1>
      </div>

      <div className="flex-1 flex flex-col gap-3 md:gap-5 max-w-4xl mx-auto w-full overflow-y-auto pb-5 relative z-10">
        {courtTypes.map((courtType, index) => {
          // Get court type image based on sport name
          const getCourtImage = (sportName) => {
            const normalizedName = sportName.toLowerCase();
            
            // Check if we have a specific image for this court type
            const availableImages = ['football', 'basketball', 'badminton', 'pickleball', 'studio'];
            
            if (availableImages.includes(normalizedName)) {
              return `/courtTypes/${normalizedName}.png`;
            }
            
            // Fall back to other.png for any unmatched types
            return '/courtTypes/other.png';
          };

          const courtImage = getCourtImage(courtType.name);

          // Use language state to choose between name and thaiName
          const displayName = language === 'th' ? courtType.thaiName : courtType.name.toUpperCase();

          return (
            <div key={index} className="relative group">
              <button
                className="relative w-full overflow-hidden rounded-3xl min-h-[120px] md:min-h-[200px] cursor-pointer transition-all duration-500 hover:shadow-2xl active:scale-95 bg-gradient-to-r from-gray-500 to-gray-600"
                onClick={() => handleCourtTypeSelect(courtType)}
                aria-label={`Select ${displayName} court`}
              >
                {/* Large court type image on the right side */}
                <div className="absolute right-0 top-0 w-1/2 h-full overflow-hidden">
                  <img
                    src={courtImage}
                    alt={`${displayName} court`}
                    className="w-full h-full object-cover opacity-30 group-hover:opacity-40 transition-opacity duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-l from-gray-500/50 to-transparent"></div>
                </div>

                {/* Shimmer effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>

                {/* Content */}
                <div className="relative z-10 p-4 md:p-8 h-full flex flex-col items-center justify-center">
                  {/* Text with glow effect - large responsive font with Thai-optimized font */}
                  <span className="text-3xl md:text-6xl lg:text-7xl xl:text-8xl font-bold text-white drop-shadow-lg leading-tight text-center font-['Noto Sans Thai', 'Sarabun', 'Kanit', sans-serif]">
                    {displayName}
                  </span>
                </div>

                {/* Border glow effect */}
                <div className="absolute inset-0 rounded-3xl border-2 border-white/20 group-hover:border-white/40 transition-all duration-500"></div>

                {/* Bottom shadow for depth */}
                <div className="absolute -bottom-1 left-4 right-4 h-4 bg-black/20 rounded-b-3xl blur-sm group-hover:blur-md transition-all duration-500"></div>
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SelectCourtType; 